import { deleteDept, getDept, getDeptDetail, getDeptDevices, getDeptTree, postDept, putDept } from '@/api/system/dept';
import CommonLayout, { CommonLayoutInstance } from '@/components/CommonLayout';
import { Form, Input, Space, InputNumber, Radio, TreeSelect, Row, Col, Card, Button, Table, TableProps, Pagination, Spin } from 'antd';
import dayjs from 'dayjs';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { CloseCircleFilled, CheckCircleFilled, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { uniqBy } from 'lodash';

const TransferTable = ({
  columns,
  selectedRowKeys,
  props,
  preserveSelectedRowKeys,
  onSelectChange,
  getCheckboxProps,
  isHideRowSelection,
}: {
  columns: { title: string; dataIndex: string }[];
  selectedRowKeys?: string[];
  props: Partial<TableProps<{ id: string; name: string; product_name?: string }>>;
  preserveSelectedRowKeys: boolean;
  onSelectChange: (value: { id: string; name: string; product_name?: string }[]) => void;
  getCheckboxProps?: (record: { id: string; name: string; product_name?: string }) => { disabled: boolean };
  isHideRowSelection?: boolean;
}) => {
  return (
    <Table<{ id: string; name: string; product_name?: string }>
      scroll={{ x: 'max-content', y: preserveSelectedRowKeys ? 400 : 500 }}
      pagination={false}
      rowKey="id"
      size="small"
      columns={columns}
      {...(isHideRowSelection
        ? {}
        : {
            rowSelection: {
              preserveSelectedRowKeys,
              selectedRowKeys,
              onChange: (...args) => {
                // console.log({ args });
                onSelectChange(args[1]);
              },
              getCheckboxProps,
            },
          })}
      {...props}
    ></Table>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DeviceTransfer = ({ value, onChange, disabled }: { disabled: boolean; value?: { id: string; name: string }[]; onChange?: (value?: { id: string; name: string }[]) => void }) => {
  const [leftSelectedRows, setLeftSelectedRows] = useState<{ id: string; name: string; product_name?: string }[]>([]);
  const [rightSelectedRows, setRightSelectedRows] = useState<{ id: string; name: string; product_name?: string }[]>([]);

  const [searchData, setSearchData] = useState<{ page: number; pageSize: number; total: number; name?: string; product_name?: string }>({ page: 1, pageSize: 10, total: 0 });
  const [dataSource, setDataSource] = useState<{ id: string; name: string; product_name?: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const getDeptDevicesList = async (params?: { page?: number; pageSize?: number; total?: number; name?: string; product_name?: string }) => {
    setLoading(true);
    try {
      const {
        result: { list, ...rest },
      } = await getDeptDevices({ ...searchData, ...params });

      setDataSource(list || []);
      setSearchData((pre) => ({ ...pre, ...rest }));
    } catch {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    !disabled && getDeptDevicesList();
  }, []);

  return (
    <Row align={'middle'} wrap={false} gutter={[8, 8]}>
      {!disabled && (
        <>
          <Col flex="auto">
            <Card title={`待选-${leftSelectedRows.length || 0}项`} size="small" styles={{ body: { padding: '10px 0', height: '520px' } }}>
              <Space size={'middle'} direction="vertical" style={{ width: '100%' }}>
                <Space style={{ margin: '0 10px' }}>
                  <Input
                    placeholder="请输入设备名称"
                    onChange={({ target: { value } }) => {
                      setSearchData((pre) => ({ ...pre, name: value }));
                    }}
                    allowClear
                  ></Input>
                  <Input
                    placeholder="请输入产品名称"
                    onChange={({ target: { value } }) => {
                      setSearchData((pre) => ({ ...pre, product_name: value }));
                    }}
                    allowClear
                  ></Input>
                  <Button
                    type="primary"
                    onClick={() => {
                      getDeptDevicesList({ page: 1 });
                    }}
                    loading={loading}
                  >
                    查询
                  </Button>
                </Space>
                <Spin spinning={loading}>
                  <TransferTable
                    columns={[
                      { title: '设备名称', dataIndex: 'name' },
                      { title: '产品名称', dataIndex: 'product_name' },
                    ]}
                    props={{ dataSource }}
                    preserveSelectedRowKeys
                    onSelectChange={(rows) => {
                      setLeftSelectedRows(rows);
                    }}
                    getCheckboxProps={(record) => {
                      return { disabled: !!value?.length && !!value.find((item) => item.id === record.id) };
                    }}
                    selectedRowKeys={leftSelectedRows?.map((item) => item.id) || []}
                  ></TransferTable>
                  <div style={{ textAlign: 'right', marginTop: 6 }}>
                    <Pagination
                      {...{
                        current: searchData.page,
                        pageSize: searchData.pageSize,
                        total: searchData.total,
                        size: 'small',
                        showTotal: (total) => `共${total}项`,
                        onChange: (page, pageSize) => {
                          setSearchData((pre) => ({ ...pre, page, pageSize }));
                          getDeptDevicesList({ page, pageSize });
                        },
                      }}
                    ></Pagination>
                  </div>
                </Spin>
              </Space>
            </Card>
          </Col>
          <Col flex="none">
            <Space direction="vertical" size={8}>
              <div>
                <Button
                  type="primary"
                  disabled={!leftSelectedRows?.length}
                  size="small"
                  block
                  icon={<RightOutlined />}
                  onClick={() => {
                    onChange?.(uniqBy([...(value || []), ...leftSelectedRows.map((item) => ({ id: item.id, name: item.name }))], 'id'));
                    setLeftSelectedRows([]);
                  }}
                ></Button>
              </div>
              <div>
                <Button
                  type="primary"
                  disabled={!rightSelectedRows?.length}
                  size="small"
                  block
                  icon={<LeftOutlined />}
                  onClick={() => {
                    const idList = rightSelectedRows.map((item) => item.id);
                    onChange?.(value?.filter((item) => !idList.includes(item.id)));
                    setRightSelectedRows([]);
                  }}
                ></Button>
              </div>
            </Space>
          </Col>
        </>
      )}
      <Col flex="auto">
        <Card title={`已选中-${rightSelectedRows.length ? `${rightSelectedRows.length}/` : ''}${value?.length || 0}项`} size="small" styles={{ body: { padding: '10px 0', height: '520px' } }}>
          <TransferTable
            onSelectChange={(rows) => {
              setRightSelectedRows(rows);
            }}
            preserveSelectedRowKeys={false}
            props={{ dataSource: value }}
            columns={[{ title: '设备名称', dataIndex: 'name' }]}
            selectedRowKeys={rightSelectedRows?.map((item) => item.id) || []}
            isHideRowSelection={disabled}
          ></TransferTable>
        </Card>
      </Col>
    </Row>
  );
};

const Dept = () => {
  const CommonLayoutRef = useRef<CommonLayoutInstance<{ params: {}; model: {}; drawerParams: {}; drawerModel: {} }>>(null);
  const [deptTree, setDeptTree] = useState<{ id?: string; label?: string; children?: { id?: string; label?: string }[] }[]>([]);
  const getDeptTreeList = async () => {
    try {
      const res = await getDeptTree();
      setDeptTree([{ id: '0', label: '根目录', children: res.result || [] }]);
    } catch {
    } finally {
      // console.log('finally');
    }
  };

  useEffect(() => {
    getDeptTreeList();
  }, []);

  return (
    <CommonLayout
      ref={CommonLayoutRef}
      breadcrumb={{
        title: '部门管理',
        describe: '部门管理页面提供数据筛选、新增部门、删除等功能模块，用于管理平台菜单。',
      }}
      searchFormChildren={
        <Space>
          <Form.Item name="dept_name">
            <Input placeholder="部门名称" allowClear></Input>
          </Form.Item>
        </Space>
      }
      columns={[
        // {
        //   dataIndex: 'parent_id',
        //   title: '上级部门',
        //   render: (v) => (!v ? '根目录' : v),
        // },
        {
          dataIndex: 'dept_name',
          title: '部门名称',
        },
        {
          dataIndex: 'leader',
          title: '负责人',
        },
        {
          dataIndex: 'phone',
          title: '电话',
        },
        {
          dataIndex: 'email',
          title: '邮箱',
        },
        {
          dataIndex: 'status',
          title: '状态',
          // render: (v: number) => ({ 1: '启用', 2: '禁用' })[v],

          // render: (v: number) => <Switch checkedChildren="启用" unCheckedChildren="禁用" checked={v === 1} />,
          render: (value: number) => {
            if (value === 2)
              return (
                <Space>
                  <CloseCircleFilled style={{ color: '#ef8e62' }} />
                  禁用
                </Space>
              );
            if (value === 1)
              return (
                <Space>
                  <CheckCircleFilled style={{ color: '#52c41a' }} />
                  启用
                </Space>
              );
            return '-';
          },
        },
        {
          dataIndex: 'sort',
          title: '排序',
        },
        {
          dataIndex: 'created',
          title: '创建时间',
          render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
        },
      ]}
      ajaxJson={{
        get: {
          ajax: getDept,
          transform: (list) => ({ list }),
          isNotToSetValues: true,
        },
        // @ts-ignore
        delete: { ajax: deleteDept },
        getTree: { ajax: getDeptTree },
      }}
      tableProps={{ pagination: false }}
      commonCbk={(key) => {
        if (key !== 'get') {
          getDeptTree();
          CommonLayoutRef.current?.reload();
        }
      }}
      drawerJson={{
        ajaxJson: {
          edit: {
            // @ts-ignore
            ajax: putDept,
            convertValue: ({ parent_id, device, ...rest }) => {
              return { ...rest, parent_id: parent_id === '0' ? '' : parent_id, deviceIds: device?.map((item: { id: string; name: string }) => item.id) || [] };
            },
          },
          add: {
            // @ts-ignore
            ajax: postDept,
            convertValue: ({ parent_id, device, ...rest }) => {
              return { ...rest, parent_id: parent_id === '0' ? '' : parent_id, deviceIds: device?.map((item: { id: string; name: string }) => item.id) || [] };
            },
            cbk: () => {},
          },

          get: {
            // @ts-ignore
            ajax: getDeptDetail,
            transform: ({ parent_id, device, ...rest }) => {
              return { ...rest, parent_id: parent_id || '0', device, deviceIds: device?.map((item: { id: string }) => item.id) || [] };
            },
          },
        },
        commonCbk: (key) => {
          if (key !== 'get') {
            getDeptTree();
            CommonLayoutRef.current?.reload();
          }
        },
        formProps: { layout: 'vertical' },
        props: { width: '1000px', destroyOnClose: true },
        renderFormChildren: ({ ajaxKey, isDetail: disabled }) => (
          <Fragment key={ajaxKey || 'edit'}>
            <Form.Item name="parent_id" label="上级部门" rules={[{ required: true }]}>
              <TreeSelect treeData={deptTree} disabled={disabled} fieldNames={{ label: 'label', value: 'id', children: 'children' }} treeDefaultExpandAll></TreeSelect>
            </Form.Item>
            <Form.Item name="dept_name" label="部门名称" rules={[{ required: true }]}>
              <Input disabled={disabled} placeholder="请输入"></Input>
            </Form.Item>
            <Form.Item name="leader" label="负责人" rules={[{ required: true }]}>
              <Input disabled={disabled} placeholder="请输入"></Input>
            </Form.Item>
            <Form.Item name="phone" label="电话" rules={[{ required: true }]}>
              <Input disabled={disabled} placeholder="请输入"></Input>
            </Form.Item>
            <Form.Item name="email" label="邮箱">
              <Input disabled={disabled} placeholder="请输入"></Input>
            </Form.Item>
            {/* <Form.Item name="deviceIds" label="设备" valuePropName="targetKeys">
              <Transfer
                showSearch={!disabled}
                showSelectAll={!disabled}
                dataSource={deviceTree}
                disabled={disabled}
                titles={['待选', '已选中']}
                locale={{ itemUnit: '项', itemsUnit: '项', searchPlaceholder: '请输入搜索内容' }}
                filterOption={(inputValue, item) => item.name.includes(inputValue) || item.product_name.includes(inputValue)}
              >
                {({ direction, filteredItems, onItemSelectAll, onItemSelect, selectedKeys: listSelectedKeys }) => {
                  // console.log(direction, filteredItems);

                  const rowSelection: TableRowSelection<{ id: string; name: string; product_name: string; title: string; key: string }> = {
                    // getCheckboxProps: (item) => ({ disabled: listDisabled || item.disabled }),
                    onSelectAll(selected, selectedRows) {
                      const treeSelectedKeys = selectedRows.map(({ key }) => key);
                      const diffKeys = selected ? difference(treeSelectedKeys, listSelectedKeys) : difference(listSelectedKeys, treeSelectedKeys);
                      onItemSelectAll(diffKeys as string[], selected);
                    },
                    onSelect({ key }, selected) {
                      onItemSelect(key as string, selected);
                    },
                    selectedRowKeys: listSelectedKeys,
                  };

                  return (
                    <Table
                      dataSource={filteredItems}
                      rowKey={'id'}
                      // pagination={false}
                      {...(disabled ? {} : { rowSelection })}
                      scroll={{ y: 500 }}
                      bordered
                      columns={
                        direction === 'left'
                          ? [
                              { title: '设备名称', dataIndex: 'name' },
                              { title: '产品名称', dataIndex: 'product_name' },
                            ]
                          : [{ title: '设备名称', dataIndex: 'name' }]
                      }
                      onRow={({ key }) => ({
                        onClick: () => {
                          if (disabled) return;
                          onItemSelect(key as string, !listSelectedKeys.includes(key as string));
                        },
                      })}
                    ></Table>
                  );
                }}
              </Transfer>
            </Form.Item> */}
            <Form.Item name="device" label="设备">
              <DeviceTransfer disabled={disabled}></DeviceTransfer>
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Radio.Group disabled={disabled}>
                <Radio value={1}>启用</Radio>
                <Radio value={2}>禁用</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="sort" label="排序">
              <InputNumber style={{ width: '100%' }} disabled={disabled}></InputNumber>
            </Form.Item>
            <Form.Item name="id" noStyle>
              <div></div>
            </Form.Item>
          </Fragment>
        ),
      }}
      operateButtonList={[
        {
          name: '编辑',
          ajaxKey: 'edit',
          props: { type: 'primary' },
          isToShowDrawer: true,
          sortData: ({ id }) => ({ id }),
          isToReload: true,
          permission: 'admin:sysDept:edit',
        },
        {
          name: '删除',
          ajaxKey: 'delete',
          props: { type: 'primary', danger: true },
          isToPopConfirm: true,
          isToAjax: true,
          popconfirmProps: { title: '是否确认删除该部门?' },
          sortData: ({ id }) => ({ id }),
          permission: 'admin:sysDept:remove',
        },
      ]}
      leftOperateButtonList={[
        {
          name: '新增',
          permission: 'admin:sysDept:add',
          ajaxKey: 'add',
          props: { type: 'primary' },
          isToShowDrawer: true,
          drawerProps: { title: '新增' },
          sortData: () => ({ parent_id: '0', status: 1 }),
        },
      ]}
    ></CommonLayout>
  );
};
export default Dept;
